import { Lock } from '@/assets/icons'
import MatterRecordReadOnlyNotice from '@/matter-asset-common/asset/MatterRecordReadOnlyNotice'
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '@/components/popover/Popover'
import { normalizeWhitespace } from '@/util/string-utils'
import type { EditingLeaseRead } from '../schemas'
import { isHeldByAnotherUser } from '../utils/is-held-by-another-user'

const kFallbackHolderName = 'Another editor'
const kPopoverArrowOffsetPx = 30

/** Limits unusually long identities, especially email fallbacks, without shortening ordinary first names. */
const kMaxCompactHolderNameLength = 32

function compactHolderName(displayName: string): string {
  const parts = displayName.split(' ')
  const abbreviated =
    displayName.includes('@') || parts.length === 1 || displayName === kFallbackHolderName
      ? displayName
      : `${parts[0]} ${Array.from(parts[parts.length - 1])[0]}.`
  const characters = Array.from(abbreviated)
  return characters.length > kMaxCompactHolderNameLength ? `${characters.slice(0, kMaxCompactHolderNameLength - 1).join('')}…` : abbreviated
}

/** Shared read-only notice for another user's live reservation. */
export default function EditingLeaseNotice({
  lease,
  shape = 'pill',
  compact = false,
}: {
  lease: EditingLeaseRead | null
  shape?: 'strip' | 'pill'
  compact?: boolean
}) {
  if (!isHeldByAnotherUser(lease)) return null
  const holderName = normalizeWhitespace(lease.holder_display_name) ?? kFallbackHolderName
  const message = 'You cannot make changes until they are done.'

  if (compact && shape === 'pill') {
    return (
      <Popover key={holderName}>
        <div className="relative inline-flex h-7 min-w-0 max-w-full">
          <span role="status" className="sr-only">
            {holderName} is editing. {message}
          </span>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="inline-flex h-7 max-w-full items-center gap-1 rounded border border-brand-100 bg-brand-50 px-2.5 text-xs font-semibold leading-4 text-brand-800 hover:bg-brand-100 data-[state=open]:bg-brand-100 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-500"
            >
              <span className="relative block h-3.5 w-3 shrink-0" aria-hidden>
                <Lock className="absolute bottom-0 right-0 size-3.5" />
              </span>
              <span className="min-w-0 truncate">{compactHolderName(holderName)}</span>
              <span className="shrink-0"> is editing</span>
            </button>
          </PopoverTrigger>
          {/* Keep the arrow near the toolbar's right edge and the popover flush with the pill. */}
          <PopoverAnchor asChild>
            <span className="pointer-events-none absolute bottom-0 h-px w-4" style={{ right: kPopoverArrowOffsetPx }} aria-hidden />
          </PopoverAnchor>
          <PopoverContent
            width="auto"
            align="end"
            alignOffset={-kPopoverArrowOffsetPx}
            sideOffset={4}
            collisionPadding={12}
            className="w-64 max-w-[calc(100vw-24px)] rounded-md p-3 text-sm font-normal leading-5 tracking-[-0.15px] text-brand-neutral-950 shadow-none [filter:drop-shadow(0px_0.6px_12.5px_rgba(0,0,0,0.10))_drop-shadow(0px_10px_5px_rgba(0,0,0,0.04))]"
          >
            <p className="break-words">
              {holderName} is editing. {message}
            </p>
          </PopoverContent>
        </div>
      </Popover>
    )
  }
  return (
    <MatterRecordReadOnlyNotice
      tone="info"
      shape={shape}
      icon={
        // Figma reserves 12px for a right-aligned 14px glyph.
        <span className="relative block h-3.5 w-3">
          <Lock className="absolute bottom-0 right-0" />
        </span>
      }
      headline={`${holderName} is editing.`}
      message={message}
    />
  )
}
